import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Sparkles,
  Heart,
  Users,
  Lightbulb,
  Rocket,
  PenTool,
  Smile,
  ChevronRight,
  Download,
  Printer,
} from "lucide-react";
import BabasahebPortrait, { BABASAHEB_IMG } from "../components/BabasahebPortrait";

// Simplified, kid-friendly stories
const STORY_CARDS = [
  {
    id: "ashoka",
    name: "Samrat Ashoka",
    badge: "The Kind Emperor",
    when: "Lived a very, very long time ago — 2,300 years!",
    img: "/images/p79lwkvj-f4c400bb-78f4-417c-93ad-866e29fe3e97.png",
    accent: "#CFA052",
    story:
      "Ashoka was once a fierce emperor who won many battles. But after one big war he saw how much sadness it caused, and his heart changed forever. He learned about the Buddha's gentle teachings and chose kindness. He built hospitals (even for animals!), planted trees along roads, and sent teachers all over Asia to share peace.",
    didYouKnow:
      "The big blue wheel on the Indian flag, the Ashoka Chakra, is named after him!",
    activity: "Draw your own peace wheel with 24 spokes, what does each spoke mean to you?",
  },
  {
    id: "shivaji",
    name: "Shivaji Maharaj",
    badge: "The Brave Ruler",
    when: "Lived around 400 years ago",
    img: "/images/bvu9v8br-1567b038-0493-4561-b578-a0ab5b0d33e2.jpeg",
    accent: "#E07A5F",
    story:
      "Shivaji was a brave king who built strong forts on hilltops and protected the common people. He believed every person, rich or poor, of any religion, deserves to live with dignity. He treated soldiers and farmers with respect and showed that a true leader serves their people.",
    didYouKnow:
      "He could ride a horse up a steep mountain fort, and his hill-forts are still standing today!",
    activity: "Build a fort from blankets, pillows or LEGO. Who would you protect inside?",
  },
  {
    id: "phule",
    name: "Mahatma Phule",
    badge: "The Teacher Who Opened Schools",
    when: "Lived 175 years ago",
    img: "/images/vqj1e8g6-804f88e5-cf8c-489e-a6b2-1bde052cec84.jpeg",
    accent: "#1B6EA1",
    story:
      "Long ago in India, girls were not allowed to go to school. Mahatma Phule and his amazing wife Savitribai said: that's not fair! Together they opened the very first school for girls in 1848. Some people threw stones at Savitribai for teaching, but she carried two saris and changed when she got to school. They never gave up.",
    didYouKnow:
      "Savitribai is called India's first woman teacher, and Phule called her his guru!",
    activity: "Say thank you to a teacher this week. They helped you learn just like Savitribai did.",
  },
  {
    id: "periyar",
    name: "Periyar",
    badge: "The Question-Asker",
    when: "Lived around 100 years ago",
    img: "/images/stg6ijqo-a859a2d4-fb40-4e44-8a5b-5f6278ccfba3.jpeg",
    accent: "#5B8C5A",
    story:
      "Periyar believed nobody should be treated as 'less than' anyone else. He kept asking, why? why? why?, about old rules that hurt people. He worked hard so girls and boys, and people of every group, would be treated with the same respect. Asking questions is how the world gets better!",
    didYouKnow:
      "Periyar lived to be 94 years old, and never stopped asking 'why?'",
    activity: "Today, ask three 'why?' questions about something you think is unfair.",
  },
  {
    id: "ambedkar",
    name: "Babasaheb Ambedkar",
    badge: "The Champion of Equality",
    when: "Born on 14 April 1891",
    img: BABASAHEB_IMG,
    accent: "#001B3A",
    story:
      "When Babasaheb was a little boy, he was made to sit on the floor at school because of his caste. But he loved learning so much that he travelled all the way to America and London to study! He became the very first person to write India's Constitution, the rulebook that says EVERY child is equal. Later he showed millions of people the path of the Buddha.",
    didYouKnow:
      "Babasaheb collected over 35,000 books, one of the biggest private libraries ever!",
    activity: "Read for 10 minutes today. Babasaheb said: 'Read, read, and read again.'",
  },
  {
    id: "annabhau",
    name: "Anna Bhau Sathe",
    badge: "The Storyteller",
    when: "Born on 1 August 1920",
    img: "/images/mu0u5i90-3c3d1e73-15f0-4597-b6df-55efedd8e23b.jpeg",
    accent: "#A33F3F",
    story:
      "Anna Bhau wrote songs and stories about the brave farmers, workers and ordinary people of his village. He went to school for only a year and a half, yet he wrote 35 whole books! He proved that a beautiful story can come from anyone, anywhere, if they have something true to say.",
    didYouKnow:
      "His songs (called 'powadas') were sung in town squares to bring people hope and courage.",
    activity: "Write a short story or song about someone in your family who works hard.",
  },
];

// Books organised UK-school style: by age group with rich descriptions
const BOOK_SHELVES = [
  {
    id: "ages-5-7",
    label: "Ages 5–7 · Read with grown-ups",
    color: "#E07A5F",
    icon: Smile,
    books: [
      {
        title: "The Three Questions",
        author: "Jon J. Muth",
        gist: "A gentle Buddhist parable retold for young children, when is the best time to do things? Who is the most important one? What is the right thing to do?",
        why: "Introduces three big Dhamma ideas through a beautiful animal story.",
      },
      {
        title: "Zen Shorts",
        author: "Jon J. Muth",
        gist: "Stillwater the panda tells three short Zen stories to three children, each one teaches a different way to be brave and kind.",
        why: "Caldecott Honor book that introduces mindfulness in a playful way.",
      },
      {
        title: "Peaceful Piggy Meditation",
        author: "Kerry Lee MacLean",
        gist: "Friendly piggies show children how to take a quiet moment, breathe and feel calm again, perfect for big feelings.",
        why: "Teaches simple meditation children can actually try.",
      },
      {
        title: "A Boy Named Bhim",
        author: "Various Indian publishers",
        gist: "The childhood story of young Babasaheb, what happened when he wasn't allowed to drink water at school, and how he turned hurt into hope.",
        why: "Simple, dignified introduction to Babasaheb's early life.",
      },
    ],
  },
  {
    id: "ages-8-12",
    label: "Ages 8–12 · Independent readers",
    color: "#CFA052",
    icon: Rocket,
    books: [
      {
        title: "Buddha at Bedtime",
        author: "Dharmachari Nagaraja",
        gist: "20 retellings of the Jataka tales, short stories the Buddha told about his past lives as animals, kings and merchants.",
        why: "A classic for ages 6–10. Each story has a thinking-question at the end.",
      },
      {
        title: "Buddha Stories",
        author: "Demi",
        gist: "Beautifully illustrated picture-book biographies of the Buddha and his teachings, told in clear, calm language.",
        why: "A warm, accessible introduction to the Buddha's life story.",
      },
      {
        title: "Prince Siddhartha: The Story of Buddha",
        author: "Jonathan Landaw & Janet Brooke",
        gist: "The full life of the Buddha, from princely childhood to enlightenment under the Bodhi tree, for older children.",
        why: "Standard text used in many UK Buddhist Sunday schools.",
      },
      {
        title: "Bhimrao Ambedkar: The Man Who Wrote India's Constitution",
        author: "Various biographies (Children's Book Trust, India)",
        gist: "Babasaheb's life, schoolboy to LSE doctorate to Constitution-writer, told for middle-grade readers.",
        why: "Perfect for Year 5–7 children to understand civic courage.",
      },
      {
        title: "Shivaji the Great Maratha",
        author: "Amar Chitra Katha",
        gist: "Vivid graphic-novel biography of Chhatrapati Shivaji, his hill forts, his battles, his sense of justice.",
        why: "ACK comics are a beloved gateway into Indian history.",
      },
    ],
  },
  {
    id: "ages-13-plus",
    label: "Ages 13+ · Teen readers",
    color: "#1B6EA1",
    icon: Lightbulb,
    books: [
      {
        title: "Bhimayana: Experiences of Untouchability",
        author: "Srividya Natarajan & S. Anand (illus. Pardhan Gond artists)",
        gist: "A breathtaking graphic novel of Babasaheb's life rendered in Pardhan Gond folk-art style, perfect for older teens.",
        why: "Award-winning. Brings caste and dignity into the conversation honestly.",
      },
      {
        title: "Old Path White Clouds",
        author: "Thich Nhat Hanh",
        gist: "The full story of the Buddha's life retold in 81 short, gentle chapters by one of the modern world's great Buddhist teachers.",
        why: "A lifetime book. Many start it as teenagers and re-read it as adults.",
      },
      {
        title: "Annihilation of Caste (Annotated, Navayana edition)",
        author: "Dr. B.R. Ambedkar (intro by Arundhati Roy)",
        gist: "Babasaheb's most important speech, the one he wrote but was never allowed to deliver. The annotated edition makes it accessible to teens.",
        why: "Foundational. Best read alongside an adult.",
      },
      {
        title: "The Story of My Experiments with Untouchability",
        author: "Various essays in The Essential Ambedkar (Penguin)",
        gist: "Selected short essays by Babasaheb, readable, powerful, and short enough to discuss one at a time.",
        why: "Easy entry into Babasaheb's own writing.",
      },
    ],
  },
  {
    id: "family",
    label: "For grown-ups & family reading",
    color: "#001B3A",
    icon: Users,
    books: [
      {
        title: "The Buddha and His Dhamma",
        author: "Dr. B.R. Ambedkar",
        gist: "Babasaheb's own retelling of the Buddha's life and teachings, written specially for the modern Ambedkarite Buddhist community.",
        why: "The foundational text of Navayana Buddhism. A must-have at home.",
      },
      {
        title: "Ambedkar: A Life",
        author: "Shashi Tharoor",
        gist: "A clear, recent biography of Babasaheb, short enough to finish in a weekend but rich in detail.",
        why: "Excellent first biography for parents new to the subject.",
      },
      {
        title: "Caste Matters",
        author: "Suraj Yengde",
        gist: "A young Dalit scholar's powerful, personal book on caste, dignity and the global Ambedkarite movement.",
        why: "Essential for diaspora parents wanting to discuss caste with teens.",
      },
      {
        title: "Why I Am a Buddhist",
        author: "Stephen T. Asma",
        gist: "A gentle, thoughtful introduction to Buddhist philosophy for parents who want a clear, modern entry point.",
        why: "Great companion to traditional Dhamma study.",
      },
      {
        title: "Slave: My True Story",
        author: "Mende Nazer (linked themes)",
        gist: "For older teens and parents, modern accounts of bondage and dignity that connect to Babasaheb's vision of universal freedom.",
        why: "Helps connect Babasaheb's struggle to global human rights today.",
      },
    ],
  },
];

const FUN_FACTS = [
  "The Buddha was a real prince named Siddhartha who lived about 2,500 years ago in northern India.",
  "Babasaheb spoke 11 languages and earned 32 degrees and qualifications.",
  "The lotus flower grows in muddy water but stays clean, that's why it's a symbol of pure thoughts.",
  "Samrat Ashoka built 84,000 stupas (Buddhist monuments) across his empire.",
  "Savitribai Phule wrote poetry that's still taught in Indian schools today.",
  "There are around 535 million Buddhists in the world, that's more than the whole population of Europe!",
];

export default function ChildrensCorner() {
  const [openShelf, setOpenShelf] = useState("ages-5-7");
  const [factIndex, setFactIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setFactIndex((i) => (i + 1) % FUN_FACTS.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative bg-navy text-pearl overflow-hidden" data-testid="children-hero">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 80%, rgba(207,160,82,0.4), transparent 40%), radial-gradient(circle at 80% 20%, rgba(224,122,95,0.3), transparent 40%)",
          }}
        />
        <div className="relative section-container py-20">
          <p className="eyebrow text-gold">Children's Corner</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Big stories. <span className="text-gold italic">Little hearts.</span>
          </h1>
          <p className="mt-6 text-pearl/80 max-w-2xl text-lg leading-relaxed">
            Welcome, young friends and parents! Here you'll meet the brave reformers
            who shaped our world, in stories made just for children, plus a reading shelf
            with some of our favourite books on the Buddha and Babasaheb's teachings.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#stories" className="px-6 py-3 bg-gold text-navy hover:bg-gold-light rounded-full text-sm font-medium" data-testid="cc-stories-cta">
              Read the stories
            </a>
            <a href="#library" className="px-6 py-3 border border-pearl/30 text-pearl hover:bg-pearl/10 rounded-full text-sm" data-testid="cc-library-cta">
              Visit the library
            </a>
          </div>
        </div>
      </section>

      {/* DID YOU KNOW BAR */}
      <section className="bg-saffron/10 border-y border-saffron/20 py-5" data-testid="children-fact-bar">
        <div className="section-container flex items-center gap-4">
          <Sparkles className="text-saffron shrink-0" size={22} />
          <p className="font-display italic text-navy text-base sm:text-lg">
            <span className="text-saffron font-semibold not-italic mr-2">Did you know?</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={factIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.4 }}
              >
                {FUN_FACTS[factIndex]}
              </motion.span>
            </AnimatePresence>
          </p>
        </div>
      </section>

      {/* STORY CARDS */}
      <section id="stories" className="py-20 bg-pearl scroll-mt-24" data-testid="children-stories">
        <div className="section-container">
          <p className="eyebrow">Meet the heroes</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy max-w-3xl">
            Six big-hearted people who changed the world.
          </h2>
          <p className="mt-4 text-slate-700 max-w-2xl">
            Tap any card to read their story, learn a fun fact, and find a kind activity to try at home.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {STORY_CARDS.map((s, i) => (
              <StoryCard key={s.id} story={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* LIBRARY */}
      <section id="library" className="py-20 bg-white scroll-mt-24" data-testid="children-library">
        <div className="section-container">
          <p className="eyebrow">The reading shelf</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy max-w-3xl">
            Books we love, by age group.
          </h2>
          <p className="mt-4 text-slate-700 max-w-3xl">
            Just like a UK school library, we've sorted books by age. Borrow these from your local library
            (most are available at Birmingham, Leeds, Manchester and London public libraries), or order online.
            <span className="block mt-2 italic text-slate-500 text-sm">
              We are not affiliated with any publisher, these are simply our community recommendations.
            </span>
          </p>

          <div className="mt-10 flex flex-wrap gap-3" role="tablist" data-testid="shelf-tabs">
            {BOOK_SHELVES.map((sh) => {
              const Icon = sh.icon;
              const active = openShelf === sh.id;
              return (
                <button
                  key={sh.id}
                  onClick={() => setOpenShelf(sh.id)}
                  data-testid={`shelf-tab-${sh.id}`}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-full border transition-all ${
                    active
                      ? "bg-navy text-pearl border-navy shadow-md"
                      : "bg-pearl text-navy border-navy/15 hover:border-gold"
                  }`}
                  style={active ? { backgroundColor: sh.color, borderColor: sh.color } : undefined}
                >
                  <Icon size={16} />
                  <span className="text-xs sm:text-sm uppercase tracking-widest">{sh.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-10">
            <AnimatePresence mode="wait">
              {BOOK_SHELVES.filter((s) => s.id === openShelf).map((sh) => (
                <motion.div
                  key={sh.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                  {sh.books.map((b, i) => (
                    <BookCard key={i} book={b} accent={sh.color} />
                  ))}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="py-20 bg-pearl" data-testid="children-activities">
        <div className="section-container">
          <p className="eyebrow">Family activities</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy max-w-3xl">
            Five gentle ideas for the weekend.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: PenTool, title: "Draw a Dharma Wheel", body: "Draw your own peace wheel with 24 spokes. What does each spoke stand for in your family?" },
              { icon: Heart, title: "A Letter of Thanks", body: "Write a short letter to a grandparent, teacher or neighbour. Babasaheb said gratitude is a form of strength." },
              { icon: BookOpen, title: "Read for 10 Minutes", body: "Read a story together every evening, Buddha tales, Jataka tales, or any reformer's biography." },
              { icon: Smile, title: "Quiet Breath", body: "Sit quietly for 1 minute before dinner. Notice your breath. That's how the Buddha taught children to begin meditation." },
              { icon: Lightbulb, title: "Ask 3 'Why?' Questions", body: "Like Periyar, pick something at school or in your community that doesn't seem fair, and ask why." },
              { icon: Users, title: "Help Someone Today", body: "Help a younger sibling, set the table, or share a snack. Small acts of kindness build a kinder world." },
            ].map((a) => {
              const I = a.icon;
              return (
                <div key={a.title} className="bg-white border border-navy/10 p-6 rounded-2xl hover:-translate-y-1 hover:shadow-md transition-all">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-saffron/15 text-saffron mb-4">
                    <I size={20} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-2xl text-navy">{a.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{a.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARENT NOTE */}
      <section className="py-20 bg-navy text-pearl" data-testid="children-parent-note">
        <div className="section-container max-w-3xl text-center">
          <p className="eyebrow text-gold">A note for parents</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl">
            Talk gently. Listen always. Read together.
          </h2>
          <p className="mt-6 text-pearl/80 leading-relaxed">
            The reformers we honour at ABGN-UK lived through hardships we hope our children never face.
            When discussing topics like caste, courage and equality, use age-appropriate language and
            invite questions. The goal isn't to impress facts onto our children, it's to build an
            inner sense of dignity, kindness and curiosity that they will carry for life.
          </p>
          <p className="mt-6 text-gold font-display italic text-2xl">
            "A great man is the servant of society."
            <span className="block not-italic mt-2 text-sm text-pearl/60 uppercase tracking-[0.2em]">
              — Babasaheb Dr. B.R. Ambedkar
            </span>
          </p>
          <a
            href="/children/booklet"
            data-testid="cc-booklet-bottom-cta"
            className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-navy hover:bg-gold-light rounded-full font-medium"
          >
            <Printer size={18} /> Take it with you, print or save the Reading Booklet
          </a>
        </div>
      </section>
    </>
  );
}

function StoryCard({ story, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
      className="bg-white rounded-3xl border border-navy/10 overflow-hidden shadow-sm hover:shadow-xl transition-shadow"
      data-testid={`story-card-${story.id}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-5">
        <div className="sm:col-span-2 relative aspect-square sm:aspect-auto">
          {story.id === "ambedkar" ? (
            <BabasahebPortrait className="rounded-none border-0 h-full" aspect="aspect-auto sm:h-full" />
          ) : (
            <img src={story.img} alt={story.name} className="w-full h-full object-cover" loading="lazy" />
          )}
        </div>
        <div className="sm:col-span-3 p-6 sm:p-8 flex flex-col">
          <span
            className="self-start text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full text-white"
            style={{ backgroundColor: story.accent }}
          >
            {story.badge}
          </span>
          <h3 className="mt-4 font-display text-3xl text-navy leading-tight">{story.name}</h3>
          <p className="mt-1 text-xs text-slate-500 italic">{story.when}</p>
          <p className="mt-4 text-slate-700 leading-relaxed">{story.story}</p>

          <button
            onClick={() => setOpen((s) => !s)}
            data-testid={`story-toggle-${story.id}`}
            className="mt-5 self-start inline-flex items-center gap-1.5 text-saffron hover:text-saffron-light text-sm font-medium"
          >
            {open ? "Hide" : "Discover more"}
            <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
          </button>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-4 p-4 rounded-2xl bg-saffron/10 border border-saffron/30">
                  <p className="text-xs uppercase tracking-widest text-saffron font-medium">Did you know?</p>
                  <p className="mt-1.5 text-sm text-navy">{story.didYouKnow}</p>
                </div>
                <div className="mt-3 p-4 rounded-2xl bg-gold/15 border border-gold/40">
                  <p className="text-xs uppercase tracking-widest text-gold-dark font-medium">Try this at home</p>
                  <p className="mt-1.5 text-sm text-navy">{story.activity}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

function BookCard({ book, accent }) {
  const q = encodeURIComponent(book.title);
  const worldcat = `https://search.worldcat.org/search?q=${q}`;
  const britishLibrary = `https://bll01.primo.exlibrisgroup.com/discovery/search?vid=44BL_INST:BLL01&query=any,contains,${q}`;
  return (
    <article className="bg-pearl rounded-2xl border border-navy/10 p-6 hover:-translate-y-1 hover:shadow-md transition-all flex flex-col h-full">
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-14 rounded-sm shrink-0 flex items-center justify-center text-pearl"
          style={{
            background: `linear-gradient(180deg, ${accent} 0%, ${accent}cc 100%)`,
            boxShadow: "inset 1px 0 0 rgba(255,255,255,0.2)",
          }}
        >
          <BookOpen size={16} />
        </div>
        <div>
          <h3 className="font-display text-xl text-navy leading-tight">{book.title}</h3>
          <p className="text-xs text-saffron mt-0.5">by {book.author}</p>
        </div>
      </div>
      <p className="mt-4 text-sm text-slate-700 leading-relaxed">{book.gist}</p>
      <p className="mt-3 text-xs text-slate-500 italic border-t border-navy/5 pt-3">
        <span className="not-italic uppercase tracking-widest text-[10px] text-navy/60 mr-2">Why we love it</span>
        {book.why}
      </p>
      <div className="mt-4 pt-3 border-t border-navy/5 flex items-center gap-2 mt-auto" data-testid={`book-borrow-${book.title.slice(0,12).replace(/[^a-z0-9]/gi,'').toLowerCase()}`}>
        <span className="text-[10px] uppercase tracking-widest text-navy/60 mr-1">Borrow:</span>
        <a
          href={worldcat}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs px-3 py-1.5 rounded-full bg-navy text-pearl hover:bg-navy-light inline-flex items-center gap-1"
        >
          WorldCat
        </a>
        <a
          href={britishLibrary}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs px-3 py-1.5 rounded-full border border-navy/20 text-navy hover:border-gold inline-flex items-center gap-1"
        >
          British Library
        </a>
      </div>
    </article>
  );
}
